/**
 * useMarkModuleComplete — POST /me/courses/:assignmentId/modules/:moduleId/complete
 * for content modules. Idempotent server-side. On success, invalidates the
 * dashboard / courses / course-detail caches so progress bars refresh.
 *
 * The mutation accepts `{ moduleId, durationSeconds? }`. `durationSeconds` is
 * forwarded to the backend as `{ duration_seconds }`; the server ignores `0`
 * or absent values and accumulates the time across re-completions.
 *
 * For evaluation / feedback modules use the dedicated finalize / submit
 * mutations — this endpoint will 409 WRONG_MODULE_TYPE for those.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { markModuleComplete } from '../../api/me';
import { learnerKeys } from './learnerKeys';

interface MarkCompleteInput {
  moduleId: string;
  durationSeconds?: number;
}

export function useMarkModuleComplete(assignmentId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ moduleId, durationSeconds }: MarkCompleteInput) =>
      markModuleComplete(assignmentId, moduleId, durationSeconds),
    onSuccess: async () => {
      // Await the course refetch so callers using `mutateAsync` see fresh
      // progress before navigating — otherwise the linear-gate effect on
      // CourseModulePage races the cache and bounces the user back.
      await qc.invalidateQueries({ queryKey: learnerKeys.course(assignmentId) });
      qc.invalidateQueries({ queryKey: learnerKeys.dashboard });
      qc.invalidateQueries({ queryKey: learnerKeys.courses });
    },
  });
}
